
import { Vue, Component, Prop, Ref, Watch } from 'vue-property-decorator';
import axios from 'axios';
import DateRangePicker from '@gid/vue-common/components/DateRangePicker.vue';
import {
  AccountingDocument,
  AccountingDocumentStatusEnum,
  PaymentRecord,
} from '@gid/models';
import PaymentAmount from './PaymentAmount.vue';
import PaymentSelector from './PaymentSelector.vue';
import DocumentSelector from './DocumentSelector.vue';
import { AccountingDocumentTypeEnum } from '@gid/models';
import PaymentLabel from './PaymentLabel.vue';
import DocumentLabel from './DocumentLabel.vue';

import { BModal } from 'bootstrap-vue';
import { plainToClass } from 'class-transformer';
import {
  CreateQueryParams,
  SConditionAND,
  SFields,
} from '@dataui/crud-request';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * One displayed document row = one settled SIDE of a document↔payment link
 * group. `paymentIds` are the payments whose links settle this side; rows
 * not involving the opened payment are display-only (curated from the other
 * payment's view).
 */
interface ReconcileRow {
  doc: AccountingDocument;
  asCancellation: boolean;
  paymentIds: string[];
}

interface LinkSideEntry {
  documentId: string;
  paymentId: string;
  reconcileCancellation: boolean;
  persisted?: boolean;
}

function cmpDate(a, b) {
  return new Date(a.issueDate).valueOf() - new Date(b.issueDate).valueOf();
}

@Component({
  components: {
    PaymentAmount,
    PaymentSelector,
    DocumentSelector,
    PaymentLabel,
    DocumentLabel,
    DateRangePicker,
  },
})
export default class ReconcilePayment extends Vue {
  @Ref('modal') readonly modal!: BModal;

  inProgress: boolean = false;
  paymentId: string | null = null;
  initialDocuments: AccountingDocument[] = [];
  initialPayments: PaymentRecord[] = [];
  rows: ReconcileRow[] = [];
  payments: PaymentRecord[] = [];
  updates: { [key: string]: Partial<AccountingDocument> } = {};
  error: any = null;
  notice: string | null = null;
  filters: {
    payment: 'prepayment' | 'postpayment' | 'range' | null;
    documentsDateRange: {
      start: Date | null;
      end: Date | null;
    };
  } = {
    payment: null,
    documentsDateRange: {
      start: null,
      end: null,
    },
  };

  /** Unique documents of the displayed rows (a dual-side doc appears once). */
  get documents(): AccountingDocument[] {
    const seen = new Set<string>();
    const docs: AccountingDocument[] = [];
    for (const row of this.rows) {
      const key = String(row.doc.id);
      if (seen.has(key)) continue;
      seen.add(key);
      docs.push(row.doc);
    }
    return docs;
  }

  get documentsTotal(): number {
    // Foreign rows (a side settled by a payment outside the displayed group,
    // e.g. the invoice side of a document whose Storno this payment refunds)
    // are informational — their amount is covered by the OTHER payment's
    // group, so they must not count toward this group's documents total.
    const groupPaymentIds = new Set(
      (this.payments ?? []).map((p: any) => String(p.id)),
    );
    const totalCents = this.rows.reduce((sum, row) => {
      if (!row.paymentIds.some((pid) => groupPaymentIds.has(pid))) {
        return sum;
      }
      const gross = row.doc.totalAmount + row.doc.vatAmount;
      const creditSign =
        row.doc.type === AccountingDocumentTypeEnum.CREDIT_NOTE ? -1 : 1;
      const cancelSign = row.asCancellation ? -1 : 1;
      return sum + Math.round(100 * gross * creditSign * cancelSign);
    }, 0);
    return totalCents / 100;
  }

  inferReconcileCancellation(doc: AccountingDocument): boolean {
    const allReferences = this.payments.flatMap(
      (p: any) => p.documentNumbers || [],
    );
    if (
      doc.cancellationNumber &&
      allReferences.includes(doc.cancellationNumber)
    ) {
      return true;
    }
    // Mirrors PaymentReconciler.inferReconcileCancellationMap: an *active*
    // cancellation must not flip the link off the original side by default;
    // only an already-settled cancellation (PAID, original reverted to SENT)
    // keeps the cancellation side. The operator switches sides explicitly
    // via the swap button.
    return (
      doc.cancellationStatus === AccountingDocumentStatusEnum.PAID &&
      doc.status === AccountingDocumentStatusEnum.SENT
    );
  }
  get paymentsTotal(): number {
    const totalCents = this.payments.reduce(
      (sum, payment) => sum + Math.round(100 * payment.amount),
      0,
    );
    return totalCents / 100;
  }

  get rowsSorted(): ReconcileRow[] {
    return [...this.rows].sort((a, b) => cmpDate(a.doc, b.doc));
  }

  get paymentsSorted() {
    return this.payments.sort(cmpDate);
  }

  get hasUpdates() {
    return Object.keys(this.updates).length > 0;
  }

  get initialJobIds() {
    return new Set(
      this.initialDocuments.flatMap((doc) => doc.jobs.map((j) => j.id)),
    );
  }

  get jobs() {
    return this.documents
      .flatMap((doc) =>
        doc.jobs.map((j) => ({
          ...j,
          isNew: !this.initialJobIds.has(j.id),
        })),
      )
      .sort((a, b) => (a.isNew as any) - (b.isNew as any));
  }

  get documentsQueryParams() {
    if (this.filters.payment === 'range') {
      const search: Array<SFields | SConditionAND> = [];
      if (this.filters.documentsDateRange.start) {
        search.push({
          issueDate: {
            $gte: this.filters.documentsDateRange.start.toISOString(),
          },
        });
      }
      if (this.filters.documentsDateRange.end) {
        search.push({
          issueDate: {
            $lte: this.filters.documentsDateRange.end.toISOString(),
          },
        });
      }
      return { search };
    }

    const op =
      this.filters.payment === 'prepayment'
        ? '$lte'
        : this.filters.payment === 'postpayment'
        ? '$gte'
        : null;
    if (op && this.paymentsSorted.length > 0) {
      const earliestPaymentDate = this.paymentsSorted
        .map((p) => p.date)
        .sort(cmpDate)[0];
      return {
        search: {
          issueDate: {
            [op]: earliestPaymentDate.toISOString(),
          },
        },
      } as CreateQueryParams;
    }
    return {};
  }

  rowKey(row: ReconcileRow): string {
    return `${row.doc.id}:${row.asCancellation ? 'stt' : 'orig'}`;
  }

  rowNewStatus(row: ReconcileRow): string | undefined {
    const update = this.updates[row.doc.id];
    if (!update) return undefined;
    return (
      (row.asCancellation ? update.cancellationStatus : update.status) ??
      undefined
    );
  }

  docRows(documentId): ReconcileRow[] {
    return this.rows.filter((r) => String(r.doc.id) === String(documentId));
  }

  /** A row is the opened payment's own when no other payment settles it. */
  rowIsEditable(row: ReconcileRow): boolean {
    return row.paymentIds.every((pid) => pid === String(this.paymentId));
  }

  isForeignRow(row: ReconcileRow): boolean {
    return !row.paymentIds.includes(String(this.paymentId));
  }

  foreignPaymentLabel(row: ReconcileRow): string {
    return row.paymentIds
      .filter((pid) => pid !== String(this.paymentId))
      .join(', ');
  }

  docHasForeignRows(documentId): boolean {
    return this.docRows(documentId).some((r) => !this.rowIsEditable(r));
  }

  /**
   * Rows involving another payment cannot be removed here: the group save
   * cannot sever a single foreign edge or side row — that curation belongs
   * to the other payment's view (or gid-cli's payment-scoped mode).
   */
  canRemoveRow(row: ReconcileRow): boolean {
    return this.rowIsEditable(row) && !this.docHasForeignRows(row.doc.id);
  }

  /**
   * The OPENED payment's share of a row can be swapped whenever the document
   * has two sides and that share is not already sitting on the target side.
   *
   * Deliberately not gated on rowIsEditable: a row shared with another payment
   * (both settling the same side) still contains a link that belongs to the
   * opened payment, and swapping re-sides only that one — `linkSides` carries
   * an entry per (document, payment), so the foreign payment keeps its side.
   * Requiring sole ownership made the paid-then-clawed-back case unfixable: an
   * invoice paid on one avis and reversed on another has both links on the
   * cancellation side, they collapse into a single shared row, and it could
   * then be neither swapped (button hidden) nor re-added (duplicate side).
   */
  canSwapRow(row: ReconcileRow): boolean {
    if (!row.doc.cancellationNumber) return false;
    const openedId = String(this.paymentId);
    if (!row.paymentIds.includes(openedId)) return false;
    return !this.docRows(row.doc.id).some(
      (other) =>
        other !== row &&
        other.asCancellation !== row.asCancellation &&
        other.paymentIds.includes(openedId),
    );
  }

  openForPayment(paymentId) {
    this.paymentId = paymentId;
    this.loadPreview();
    this.modal.show();
  }

  modalClosed() {
    this.$emit('close');
  }

  loadPreview() {
    this.inProgress = true;
    this.error = null;
    axios
      .get(`/documents-api/accounting/payments/reconcile/${this.paymentId}`)
      .then((result) => {
        this.initialPayments = plainToClass(
          PaymentRecord,
          result.data.paymentRecords as any[],
        );
        this.initialDocuments = plainToClass(
          AccountingDocument,
          result.data.documents as any[],
        );
        this.payments = this.initialPayments.slice();
        const linkSides: LinkSideEntry[] = result.data.linkSides || [];
        if (linkSides.length) {
          // One row per (document, side); a dual-side link renders twice.
          const byKey = new Map<string, ReconcileRow>();
          for (const entry of linkSides) {
            const doc = this.initialDocuments.find(
              (d) => String(d.id) === String(entry.documentId),
            );
            if (!doc) continue;
            const key = `${entry.documentId}:${!!entry.reconcileCancellation}`;
            const existing = byKey.get(key);
            const row: ReconcileRow = existing || {
              doc,
              asCancellation: !!entry.reconcileCancellation,
              paymentIds: [],
            };
            if (!existing) {
              byKey.set(key, row);
            }
            row.paymentIds.push(String(entry.paymentId));
          }
          this.rows = [...byKey.values()];
        } else {
          // Pre-phase-2 server: one row per document from the legacy map.
          const serverMap: Record<string, boolean> =
            result.data.reconcileCancellation || {};
          this.rows = this.initialDocuments.map((doc) => ({
            doc,
            asCancellation:
              serverMap[doc.id] !== undefined
                ? serverMap[doc.id]
                : this.inferReconcileCancellation(doc),
            paymentIds: [String(this.paymentId)],
          }));
        }
        this.updates = result.data.updates
          ? result.data.updates.reduce(
              (u, doc) => ({ ...u, [doc.id]: doc }),
              {},
            )
          : {};
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * The reconcile request body. `linkSides` carries the full per-link side
   * state of the displayed rows; the legacy map is kept for pre-phase-2
   * servers and only covers single-row documents (a dual-side pair cannot be
   * expressed there — omitting it keeps the persisted sides untouched).
   */
  reconcileBody() {
    const displayedPaymentIds = new Set(this.payments.map((p) => String(p.id)));
    const linkSides = this.rows.flatMap((row) =>
      row.paymentIds
        .filter((pid) => displayedPaymentIds.has(pid))
        .map((paymentId) => ({
          documentId: String(row.doc.id),
          paymentId,
          reconcileCancellation: row.asCancellation,
        })),
    );
    // The submission reflects the rows as edited: a document whose last row
    // was removed leaves the group entirely (this.documents still holds the
    // load-time set).
    const submittedDocuments = [
      ...new Map(
        this.rows.map((row) => [String(row.doc.id), row.doc]),
      ).values(),
    ];
    const reconcileCancellation: Record<string, boolean> = {};
    for (const doc of submittedDocuments) {
      const docRows = this.docRows(doc.id);
      if (docRows.length === 1) {
        reconcileCancellation[doc.id] = docRows[0].asCancellation;
      }
    }
    // Payment-scoped save: `documents` carries the whole displayed group for
    // the settle math, but link curation must only touch the OPENED payment —
    // linkDocumentIds is its own desired doc set (the rows attributed to it).
    // A group-mode save would bipartite-link every displayed payment to every
    // displayed doc, silently re-entangling groups that share documents.
    const openedId = String(this.paymentId);
    const linkDocumentIds = [
      ...new Set(
        this.rows
          .filter((row) => row.paymentIds.includes(openedId))
          .map((row) => String(row.doc.id)),
      ),
    ];
    return {
      paymentRecords: this.payments,
      documents: submittedDocuments,
      reconcileCancellation,
      linkSides,
      linkScope: 'payment',
      linkDocumentIds,
    };
  }

  updatePreview() {
    this.inProgress = true;
    this.error = null;
    axios
      .post(
        `/documents-api/accounting/payments/reconcile/preview`,
        this.reconcileBody(),
      )
      .then((result) => {
        this.updates = Array.isArray(result.data)
          ? result.data.reduce((u, doc) => ({ ...u, [doc.id]: doc }), {})
          : {};
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  save() {
    this.inProgress = true;
    this.error = null;
    axios
      .post(
        `/documents-api/accounting/payments/reconcile/${this.paymentId}`,
        this.reconcileBody(),
      )
      .then(() => {
        this.modal.hide();
      })
      .catch((error) => {
        this.error = error;
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  addPayment(payment) {
    this.payments.push(payment);
    this.updatePreview();
  }

  removePayment(payment) {
    const pid = String(payment.id);
    this.payments = this.payments.filter((p) => p.id !== payment.id);
    // Rows settled by the removed payment lose that attribution; the opened
    // payment's own edge always keeps at least one row per document.
    this.rows = this.rows
      .map((row) => ({
        ...row,
        paymentIds: row.paymentIds.filter((x) => x !== pid),
      }))
      .filter((row) => row.paymentIds.length > 0);
    this.updatePreview();
  }

  addDocument(document, selectedSide?: boolean) {
    // selectedSide carries the DocumentSelector context (the user picked the
    // doc under its STT number, or under its invoice number), which inference
    // alone cannot see — the payment's parsed documentNumbers rarely contain
    // cancellation numbers. It is a tri-state: `undefined` means the search
    // text picked neither side, and only then may inference decide.
    //
    // This must NOT be an OR. Inference returns true for exactly the
    // (sent, paid) pair, which is the state a document is in *because* its
    // original side was never settled — so ORing it made the original side
    // unreachable for precisely the documents that needed it, and adding
    // one was rejected as an already-linked duplicate.
    const side =
      typeof selectedSide === 'boolean'
        ? selectedSide
        : this.inferReconcileCancellation(document);
    const existingRows = this.docRows(document.id);
    if (existingRows.some((r) => r.asCancellation === side)) {
      const sideLabel = side
        ? this.$t('accounting.reconcile.side.cancellation')
        : this.$t('accounting.reconcile.side.original');
      this.notice = this.$t('accounting.reconcile.side-already-linked', {
        number: document.number,
        side: sideLabel,
      }) as string;
      return;
    }
    // Same DB row, other side already displayed: the original and its
    // cancellation share one accounting_document record — adding the second
    // side turns it into a net-zero pair on this group (GID-2676 phase 2).
    const doc = existingRows.length ? existingRows[0].doc : document;
    this.notice = existingRows.length
      ? (this.$t('accounting.reconcile.second-side-added', {
          number: document.number,
        }) as string)
      : null;
    this.rows.push({
      doc,
      asCancellation: side,
      paymentIds: [String(this.paymentId)],
    });
    this.updatePreview();
  }

  toggleReconcileSide(row: ReconcileRow) {
    if (!this.canSwapRow(row)) return;
    const openedId = String(this.paymentId);
    const targetSide = !row.asCancellation;
    const foreignPaymentIds = row.paymentIds.filter((pid) => pid !== openedId);
    if (!foreignPaymentIds.length) {
      // The opened payment owns the row outright — flip it in place.
      row.asCancellation = targetSide;
    } else {
      // Shared row: detach ONLY the opened payment onto the other side, so the
      // foreign payment's link keeps the side it actually settles. Merge into
      // an existing target-side row if the document already has one, otherwise
      // start one.
      row.paymentIds = foreignPaymentIds;
      const target = this.docRows(row.doc.id).find(
        (other) => other !== row && other.asCancellation === targetSide,
      );
      if (target) {
        target.paymentIds.push(openedId);
      } else {
        this.rows.push({
          doc: row.doc,
          asCancellation: targetSide,
          paymentIds: [openedId],
        });
      }
    }
    this.notice = null;
    this.updatePreview();
  }

  removeRow(row: ReconcileRow) {
    if (!this.canRemoveRow(row)) return;
    this.rows = this.rows.filter((r) => r !== row);
    this.notice = null;
    this.updatePreview();
  }
}
